import React, { useState, useMemo, useEffect, useRef, useContext, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createBasketAndPlaceTopic, moveTopic, fetchWorkspaceDetails, setBaskets } from '../../store/workspacesSlice';
import Header from './components/Header';
import Searchbar from './components/Searchbar';
import BasketGrid from './components/BasketGrid';
import MoveButton from './components/MoveButton';
import TopicInfo from './components/TopicInfo';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import RelationDescriptionModal from './components/RelationDescriptionModal';
import BasketPlacementCelebrationOverlay from './components/BasketPlacementCelebrationOverlay';
import BasketSelectionExamplesModal from './components/BasketSelectionExamplesModal';
import {
  shouldShowRotationalOverlay,
  applyPlacementCelebrationContinue,
  applyPlacementCelebrationRemind,
} from '../../lib/basketPlacementCelebration';
import { TipBoxCarousel } from '../TipBox';
import { Check } from 'lucide-react';
import { getBrowserTimeZone } from '../../lib/browserTimeZone';
import { UserContext } from '../../UserContext';
import posthog from '../../posthogClient';
import {
  countBsiReviewableTopics,
  countReviewableGridTopics,
  REVIEW_SESSION_THRESHOLD_MS,
  trackBsiReviewSession,
} from '../../lib/trackBsiReviewSession';
import { partitionBasketsBySearch } from '../../lib/basketSearch';
import { captureConnectionsEvent } from '../../lib/captureConnectionsEvent';
import { supabase } from '../../supabaseClient';

const StrongBasketCriterionItem = ({ title, children }) => (
  <li className="flex gap-3">
    <span
      className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 border-[#8B4513] bg-[#8B4513] text-white"
      aria-hidden="true"
    >
      <Check className="h-3 w-3" strokeWidth={3} />
    </span>
    <div className="min-w-0 space-y-1">
      <p className="font-semibold text-neutral-900">{title}</p>
      <p className="text-sm text-neutral-800 leading-relaxed">{children}</p>
    </div>
  </li>
);

const BASKET_FILTERS = {
  ALL: 'All Baskets',
  SINGLE: 'Single Item',
  DOUBLE: 'Two Items',
  MULTIPLE: 'Three or More',
  EMPTY: 'Empty'
};

const TabButton = ({ active, onClick, children, count, className = '' }) => (
  <button
    onClick={onClick}
    className={`
      relative px-3 py-1.5 text-sm font-medium transition-all duration-200
      ${active 
        ? 'text-neutral-900 bg-white shadow-sm' 
        : 'text-neutral-600 hover:text-neutral-800 hover:bg-white/50'
      }
      rounded-lg border
      ${active 
        ? 'border-neutral-900 shadow-sm' 
        : 'border-transparent hover:border-neutral-300'
      }
      ${className}
    `}
  >
    <span className="relative z-10 flex items-center gap-1.5">
      {children}
      {count !== undefined && (
        <span className={`
          px-1.5 py-0.5 text-xs rounded-full
          ${active 
            ? 'bg-butter-500 text-neutral-900' 
            : 'bg-neutral-200 text-neutral-600'
          }
        `}>
          {count}
        </span>
      )}
    </span>
  </button>
);

const extractKeywords = (conceptText) => {
  if (!conceptText) return [];
  
  // Extended stop words to filter out
  const stopWords = new Set([
    'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
    'of', 'with', 'by', 'from', 'as', 'is', 'are', 'was', 'were', 'be',
    'been', 'being', 'have', 'has', 'had', 'do', 'does', 'did', 'will',
    'would', 'should', 'could', 'may', 'might', 'must', 'can', 'this',
    'that', 'these', 'those', 'i', 'you', 'he', 'she', 'it', 'we', 'they',
    'what', 'which', 'who', 'when', 'where', 'why', 'how', 'all', 'each',
    'every', 'both', 'few', 'more', 'most', 'other', 'some', 'such', 'no',
    'nor', 'not', 'only', 'own', 'same', 'so', 'than', 'too', 'very',
    'thus', 'therefore', 'however', 'also', 'like', 'such', 'if', 'then'
  ]);
  
  // Common verbs to deprioritize
  const commonVerbs = new Set([
    'treat', 'treated', 'treating', 'use', 'used', 'using', 'show', 'shows', 'showed', 'showing',
    'cause', 'causes', 'caused', 'causing', 'require', 'requires', 'required', 'requiring',
    'occur', 'occurs', 'occurred', 'occurring', 'appear', 'appears', 'appeared', 'appearing',
    'develop', 'develops', 'developed', 'developing', 'present', 'presents', 'presented', 'presenting',
    'include', 'includes', 'included', 'including', 'contain', 'contains', 'contained', 'containing',
    'indicate', 'indicates', 'indicated', 'indicating', 'suggest', 'suggests', 'suggested', 'suggesting',
    'associate', 'associates', 'associated', 'associating', 'relate', 'relates', 'related', 'relating',
    'result', 'results', 'resulted', 'resulting', 'lead', 'leads', 'led', 'leading',
    'affect', 'affects', 'affected', 'affecting', 'involve', 'involves', 'involved', 'involving'
  ]);
  
  // Split by whitespace first to preserve decimal numbers (e.g., "0.1" stays as one word)
  // Then process each word
  const words = conceptText.trim().split(/\s+/);
  const candidates = [];
  
  // Score each word based on various factors
  words.forEach((word, wordIndex) => {
      const cleanWord = word.replace(/[^\w]/g, '').toLowerCase();
      const originalWord = word.trim();
      
      // Check if it's a number (including decimals, numbers with units, and numbers after special chars)
      // Only match numbers that:
      // 1. Start the word (with optional comparison operators ≥, ≤, <, >, =)
      // 2. Are standalone numbers
      // 3. Have valid units (lowercase letters, %, /) - not uppercase acronyms
      // This prevents matching numbers inside acronyms like "GLP-1" or "T2DM"
      
      // First check: if word starts with letters followed by a number, it's likely an acronym (e.g., T2DM, GLP-1)
      if (/^[A-Z]+[-\d]/.test(originalWord)) {
        // Skip acronyms - don't extract numbers from them
        // Continue to process as regular word
      } else {
        // Try to match a number at the start of the word
        const numberMatch = originalWord.match(/^[≥≤<>=]?\d+\.?\d*[+-]?[a-z%/]*/);
        
        if (numberMatch) {
          const numberText = numberMatch[0];
          // Only extract if it actually contains digits
          if (/\d/.test(numberText)) {
            // Additional check: if the word has uppercase letters after the number, it's likely an acronym (e.g., T2DM)
            const afterNumber = originalWord.slice(numberText.length);
            if (afterNumber && /[A-Z]/.test(afterNumber)) {
              // Likely an acronym, skip it
              // Continue to process as regular word
            } else {
              // If it's a number, prioritize it highly
              candidates.push({ 
                text: numberText, 
                score: 20, // High priority for numbers
                original: originalWord
              });
              return;
            }
          }
        }
      }
      
      // Skip if it's a stop word or too short
      if (cleanWord.length < 3 || stopWords.has(cleanWord)) {
        return;
      }
      
      // Check if it's a verb (common verb endings or in verb list)
      const isVerb = commonVerbs.has(cleanWord) || 
                     /^(ed|ing|es|s)$/.test(cleanWord.slice(-3)) ||
                     /(ate|ify|ize|ise)$/.test(cleanWord);
      
      let score = 0;
      
      // Significantly reduce score for verbs
      if (isVerb) {
        score -= 10;
      }
      
      // Higher score for longer words (often more specific/technical)
      if (cleanWord.length >= 6) score += 3;
      else if (cleanWord.length >= 5) score += 2;
      
      // Higher score for words appearing early in the text
      if (wordIndex < 5) score += 1;
      
      // Medical/technical term indicators (common prefixes)
      if (/^(anti|auto|bio|cardio|derm|endo|gastro|hemo|neuro|patho|psycho|uro)/i.test(cleanWord)) {
        score += 5;
      }
      
      // Common medical/technical suffixes
      if (/(itis|osis|emia|oma|pathy|scopy|tomy|plasty|ectomy)$/i.test(cleanWord)) {
        score += 5;
      }
      
      candidates.push({ 
        text: cleanWord, 
        score, 
        original: originalWord
      });
  });
  
  // Sort by score (highest first)
  candidates.sort((a, b) => b.score - a.score);
  
  // Remove duplicates and return top 3 single words
  const seen = new Set();
  const results = [];
  
  for (const candidate of candidates) {
    if (!seen.has(candidate.text)) {
      seen.add(candidate.text);
      results.push(candidate.text);
      if (results.length >= 3) break;
    }
  }
  
  // Return keywords as-is (no capitalization)
  return results.slice(0, 3);
};

const BasketSelectionInterface = ({
  onClose,
  onBasketSelected,
  currentBasketId,
  topic,
  workspaceId: propWorkspaceId,
  onReviewSessionLogged,
}) => {
  const dispatch = useDispatch();
  const baskets = useSelector(state => state.workspaces.currentWorkspace.baskets);
  const stateWorkspaceId = useSelector(state => state.workspaces.currentWorkspace.id);
  const currentWorkspace = useSelector(state => state.workspaces.currentWorkspace);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBasketId, setSelectedBasketId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const navigate = useNavigate();
  const isWorkspaceLoaded = useSelector(state => state.workspaces.isWorkspaceLoaded);
  const [basketFilter, setBasketFilter] = useState(BASKET_FILTERS.ALL);
  const [showRelationModal, setShowRelationModal] = useState(false);
  const [isMoveInProgress, setIsMoveInProgress] = useState(false);
  const [placementCelebration, setPlacementCelebration] = useState(null);
  const [showExamplesModal, setShowExamplesModal] = useState(false);

  const { user } = useContext(UserContext);
  // Distinguishes user typing from the programmatic keyword prefill, so
  // review-activity marking and search analytics only react to real input.
  const isUserSearchRef = useRef(false);
  const prefilledKeywordRef = useRef('');
  const prefillEventTopicsRef = useRef(new Set());
  const searchInputRef = useRef(null);
  const bsiOpenTimeRef = useRef(null);
  const userIdRef = useRef(null);
  const workspaceIdRef = useRef(null);
  const basketsRef = useRef([]);
  const currentBasketIdRef = useRef(null);
  const studyingTopicIdRef = useRef(null);
  const shouldForceReviewSessionRef = useRef(false);
  const hasLoggedReviewSessionRef = useRef(false);

  const markBsiReviewActivity = useCallback(() => {
    shouldForceReviewSessionRef.current = true;
  }, []);

  const captureBsiEvent = useCallback((eventName, properties) => {
    if (!process.env.REACT_APP_POSTHOG_KEY) return;
    posthog.capture(eventName, properties);
  }, []);

  useEffect(() => {
    userIdRef.current = user?.id ?? null;
  }, [user?.id]);

  useEffect(() => {
    basketsRef.current = baskets ?? [];
    currentBasketIdRef.current = currentBasketId ?? null;
    studyingTopicIdRef.current = topic?.id ?? null;
  }, [baskets, currentBasketId, topic?.id]);

  useEffect(() => {
    bsiOpenTimeRef.current = Date.now();
    return () => {
      const uid = userIdRef.current;
      const wsId = workspaceIdRef.current;
      const openedAt = bsiOpenTimeRef.current;
      if (!uid || openedAt == null) return;
      if (hasLoggedReviewSessionRef.current) return;

      const timeSpent = Date.now() - openedAt;
      const force = shouldForceReviewSessionRef.current;
      if (!force && timeSpent < REVIEW_SESSION_THRESHOLD_MS) return;

      const reviewableTopicCount = force
        ? countBsiReviewableTopics(
            basketsRef.current,
            currentBasketIdRef.current,
            studyingTopicIdRef.current
          )
        : countReviewableGridTopics(
            basketsRef.current,
            currentBasketIdRef.current,
            studyingTopicIdRef.current
          );

      hasLoggedReviewSessionRef.current = true;

      trackBsiReviewSession({
        userId: uid,
        workspaceId: wsId,
        timeSpentMs: timeSpent,
        reviewableTopicCount,
        force,
      }).then((logged) => {
        if (logged) onReviewSessionLogged?.();
      });
    };
  }, [onReviewSessionLogged]);

  // Prop workspace id when passed, otherwise Redux workspace id
  const workspaceId = propWorkspaceId || stateWorkspaceId;

  useEffect(() => {
    workspaceIdRef.current = workspaceId ?? null;
  }, [workspaceId]);

  useEffect(() => {
    if (searchTerm.trim() && isUserSearchRef.current) {
      markBsiReviewActivity();
    }
  }, [searchTerm, markBsiReviewActivity]);

  const handleSearchTermChange = useCallback((value) => {
    isUserSearchRef.current = true;
    setSearchTerm(value);
  }, []);

  // Keyword prefill: top-scored keyword from the topic's hard detail, falling
  // back to the first concept's keyword, then to the topic name — so the
  // search always arrives filled and the ghost card always has a name.
  const prefillKeyword = useMemo(() => {
    if (!topic) return '';
    const highlighted = topic.concepts?.find(concept => concept.is_highlighted);
    const fromHighlighted = highlighted?.text
      ? extractKeywords(highlighted.text)[0]
      : undefined;
    const fromFirstConcept = !fromHighlighted && topic.concepts?.[0]?.text
      ? extractKeywords(topic.concepts[0].text)[0]
      : undefined;
    return fromHighlighted || fromFirstConcept || topic.name || '';
  }, [topic]);

  useEffect(() => {
    if (!topic?.id || !prefillKeyword) return;
    prefilledKeywordRef.current = prefillKeyword;
    isUserSearchRef.current = false;
    setSearchTerm(prefillKeyword);
    // Land the cursor in the search field after prefill so the user can edit
    // or clear the keyword without an extra tap.
    requestAnimationFrame(() => {
      const input = searchInputRef.current;
      if (!input) return;
      input.focus();
      input.select();
    });
  }, [topic?.id, prefillKeyword]);

  console.log('Workspace ID from props:', propWorkspaceId);
  console.log('Workspace ID from state:', stateWorkspaceId);
  console.log('Using workspace ID:', workspaceId);

  // Add this useEffect to fetch workspace details if needed
  useEffect(() => {
    if (workspaceId && !isWorkspaceLoaded) {
      dispatch(fetchWorkspaceDetails(workspaceId));
    }
  }, [workspaceId, isWorkspaceLoaded, dispatch]);

  // Tab counts reflect every basket the grid can show (all but the current
  // one). Search no longer narrows these, since non-matching baskets are now
  // appended to the grid rather than hidden.
  const getFilterCounts = useMemo(() => {
    const otherBaskets = baskets.filter(
      basket => basket && basket.id !== currentBasketId
    );

    return {
      [BASKET_FILTERS.ALL]: otherBaskets.length,
      [BASKET_FILTERS.SINGLE]: otherBaskets.filter(b => (b.topics?.length || 0) === 1).length,
      [BASKET_FILTERS.DOUBLE]: otherBaskets.filter(b => (b.topics?.length || 0) === 2).length,
      [BASKET_FILTERS.MULTIPLE]: otherBaskets.filter(b => (b.topics?.length || 0) >= 3).length,
      [BASKET_FILTERS.EMPTY]: otherBaskets.filter(b => !b.topics?.length).length,
    };
  }, [baskets, currentBasketId]);

  const hasNoOtherBaskets = useMemo(
    () => baskets.filter((basket) => basket.id !== currentBasketId).length === 0,
    [baskets, currentBasketId]
  );

  const showFilterEmptyMessage =
    !hasNoOtherBaskets &&
    !searchTerm.trim() &&
    basketFilter !== BASKET_FILTERS.ALL;

  const basketSelectionProTips = useMemo(
    () => [
      {
        title: 'What makes a strong basket?',
        message: (
          <ul className="m-0 list-none space-y-4 p-0">
            <StrongBasketCriterionItem title="Topics span different subjects">
              Avoid baskets that are all one lane (for example, only cardio or only neuro). The most useful links{' '}
              <span className="font-medium">cut across the syllabus</span>.
            </StrongBasketCriterionItem>
            <StrongBasketCriterionItem title="Include at least one familiar anchor">
              A comfortable anchor pulls the harder material along with it. This anchor is what helps the rest of the group stick when you need it under pressure.
            </StrongBasketCriterionItem>
            <StrongBasketCriterionItem title="Each topic references the others">
              The link is strongest when the items explicitly reference each other. Add relationship descriptions that say how they connect, for example: &ldquo;Like Item A, it also…&rdquo; or &ldquo;Item C reminds me of this because…&rdquo;
            </StrongBasketCriterionItem>
            <StrongBasketCriterionItem title="One topic recalls the whole group — whichever topic you start from">
              If any single item can pull the others back into view, the shared hook is doing its job.
            </StrongBasketCriterionItem>
          </ul>
        ),
      },
      {
        title: 'When placing an item',
        message: (
          <>
            Tie this topic to an <strong>item already inside a basket</strong>, not only to the basket name. If nothing fits, start a new basket—your first item is waiting for future links. That <strong>active linking</strong> is what locks in the difficult detail you are working with. Say how they connect, for example: &ldquo;Like Item A, Item B has…&rdquo;
          </>
        ),
      },
    ],
    []
  );

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleExamplesModalOpen = () => {
    setShowExamplesModal(true);
  };

  const handleExamplesModalClose = () => {
    setShowExamplesModal(false);
  };

  // Baskets shown in the grid, plus how many of them actually matched the
  // search. Matches lead (priority-sorted); the rest of the baskets follow,
  // ordered least-recently-updated first so stale baskets resurface for review.
  const { gridBaskets: filteredBaskets, matchingBasketCount } = useMemo(() => {
    // First, filter out the current basket
    const otherBaskets = baskets.filter(basket => basket.id !== currentBasketId);

    // Apply item count filter
    const countFiltered = otherBaskets.filter(basket => {
      const itemCount = basket.topics?.length || 0;
      
      switch (basketFilter) {
        case BASKET_FILTERS.SINGLE:
          return itemCount === 1;
        case BASKET_FILTERS.DOUBLE:
          return itemCount === 2;
        case BASKET_FILTERS.MULTIPLE:
          return itemCount >= 3;
        case BASKET_FILTERS.EMPTY:
          return itemCount === 0;
        default:
          return true; // BASKET_FILTERS.ALL
      }
    });

    // No search: show the count-filtered baskets as-is.
    if (!searchTerm.trim()) {
      return { gridBaskets: countFiltered, matchingBasketCount: 0 };
    }

    const { matching, nonMatching, matchingBasketCount } = partitionBasketsBySearch(
      countFiltered,
      searchTerm
    );

    return {
      gridBaskets: [...matching, ...nonMatching],
      matchingBasketCount,
    };
  }, [baskets, searchTerm, currentBasketId, basketFilter]);

  const trimmedSearchTerm = searchTerm.trim();

  // Show the ghost cell for queries of 1+ characters, unless a basket with
  // exactly that name already exists (creating a duplicate is never right).
  const showGhostBasket = useMemo(() => {
    if (trimmedSearchTerm.length < 1) return false;
    const lowered = trimmedSearchTerm.toLowerCase();
    return !baskets.some(
      basket => basket?.name?.trim().toLowerCase() === lowered
    );
  }, [baskets, trimmedSearchTerm]);

  // Fire search analytics once the user stops typing. The programmatic
  // keyword prefill is excluded so this event keeps meaning "the user typed".
  useEffect(() => {
    if (!trimmedSearchTerm || !isUserSearchRef.current) return undefined;

    const timer = setTimeout(() => {
      captureBsiEvent('bsi_search_performed', {
        query_length: trimmedSearchTerm.length,
        match_count: matchingBasketCount,
      });
    }, 600);

    return () => clearTimeout(timer);
  }, [trimmedSearchTerm, matchingBasketCount, captureBsiEvent]);

  // Prefill analytics: once per topic, once the workspace is loaded so the
  // match counts reflect what the grid actually shows.
  useEffect(() => {
    if (!topic?.id || !prefillKeyword || !isWorkspaceLoaded) return;
    if (prefillEventTopicsRef.current.has(topic.id)) return;
    prefillEventTopicsRef.current.add(topic.id);

    const lowered = prefillKeyword.toLowerCase();
    const gridBaskets = baskets.filter(basket => basket && basket.id !== currentBasketId);
    const basketMatchCount = gridBaskets.filter(basket =>
      basket.name?.toLowerCase().includes(lowered)
    ).length;
    const itemMatchCount = gridBaskets.reduce((count, basket) => (
      count + (basket.topics || []).filter(basketTopic =>
        basketTopic.name?.toLowerCase().includes(lowered) ||
        basketTopic.concepts?.some(concept => concept.text?.toLowerCase().includes(lowered))
      ).length
    ), 0);

    captureBsiEvent('bsi_keyword_prefilled', {
      keyword: prefillKeyword,
      topic_id: topic.id,
      basket_match_count: basketMatchCount,
      item_match_count: itemMatchCount,
    });
  }, [topic?.id, prefillKeyword, isWorkspaceLoaded, baskets, currentBasketId, captureBsiEvent]);

  const handleBasketSelect = (basketId) => {
    if (basketId !== currentBasketId) {
      setSelectedBasketId((prevId) => (prevId === basketId ? null : basketId));
    }
  };

  const handleMove = async () => {
    if (selectedBasketId && selectedBasketId !== currentBasketId) {
      setShowRelationModal(true);
    }
  };

  const performTopicMove = async (relationDescription) => {
    markBsiReviewActivity();
    setIsMoveInProgress(true);
    try {
      const moveResult = await dispatch(moveTopic({
        topicId: topic.id,
        fromBasketId: currentBasketId,
        toBasketId: selectedBasketId,
        workspaceId,
        relationDescription: relationDescription || ''
      })).unwrap();

      const updatedBaskets = baskets.map(basket => {
        if (basket.id === currentBasketId) {
          return {
            ...basket,
            topics: basket.topics.filter(t => t.id !== topic.id),
            updated_at: new Date().toISOString()
          };
        }
        if (basket.id === selectedBasketId) {
          return {
            ...basket,
            topics: [...basket.topics, moveResult.topic],
            updated_at: new Date().toISOString()
          };
        }
        return basket;
      });

      const targetBasketIndex = updatedBaskets.findIndex(b => b.id === selectedBasketId);
      if (targetBasketIndex !== -1) {
        const [targetBasket] = updatedBaskets.splice(targetBasketIndex, 1);
        updatedBaskets.unshift(targetBasket);
      }

      dispatch(setBaskets(updatedBaskets));

      captureBsiEvent('topic_placed', { method: 'existing_basket' });
      void supabase.auth
        .getSession()
        .then(({ data: { session } }) => {
          if (!session?.access_token) return;

          return fetch('/api/brevo-contact', {
            method: 'POST',
            keepalive: true,
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ action: 'sync', timezone: getBrowserTimeZone() }),
          }).then((response) => {
            if (!response.ok) {
              console.warn('Brevo placement sync failed:', response.status);
            }
          });
        })
        .catch((error) => {
          console.warn('Brevo placement sync error:', error);
        });
    } catch (error) {
      console.error('Failed to move topic:', error);
      toast.error(error.message || 'Failed to move topic');
      throw error;
    } finally {
      setIsMoveInProgress(false);
    }
  };

  const navigateToWorkspaceBaskets = (scrollToBasketId) => {
    navigate(`/workspace/${workspaceId}`, {
      state: {
        activeTab: 'Baskets',
        workspaceName: currentWorkspace?.name || 'Workspace',
        lastFetchedAt: Date.now(),
        shouldScrollToBasket: scrollToBasketId
      }
    });
  };

  const finishMoveAndReturnToWorkspace = () => {
    setShowRelationModal(false);
    onClose();
    navigateToWorkspaceBaskets(selectedBasketId);
  };

  const finishPlacementCelebrationNavigation = () => {
    const scrollToBasketId = placementCelebration?.scrollToBasketId;
    setPlacementCelebration(null);
    onClose();
    navigateToWorkspaceBaskets(scrollToBasketId);
  };

  const handlePlacementCelebrationContinue = () => {
    applyPlacementCelebrationContinue(user?.id);
    finishPlacementCelebrationNavigation();
  };

  const handlePlacementCelebrationRemind = () => {
    applyPlacementCelebrationRemind(user?.id);
    finishPlacementCelebrationNavigation();
  };

  const handleBasketMenuClick = (basket, action) => {
    // Implement the logic for handling basket menu actions
    switch (action) {
      case 'edit':
        // Handle edit action
        console.log('Edit basket:', basket.id);
        break;
      case 'delete':
        // Handle delete action
        console.log('Delete basket:', basket.id);
        break;
      case 'share':
        // Handle share action
        console.log('Share basket:', basket.id);
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  const handleGhostBasketCreate = async () => {
    const basketName = searchTerm.trim();
    if (!basketName || isCreating) return;

    const matchCountAtCreation = matchingBasketCount;

    try {
      markBsiReviewActivity();
      setIsCreating(true);

      // One transactional thunk: basket insert + topic placement land in
      // Redux in a single dispatch, so the grid never flashes an empty basket.
      const { basket: createdBasket } = await dispatch(createBasketAndPlaceTopic({
        workspaceId,
        basketName,
        topicId: topic.id,
      })).unwrap();

      captureBsiEvent('bsi_ghost_basket_created', {
        basket_name: basketName,
        topic_id: topic.id,
        match_count_at_creation: matchCountAtCreation,
        matched_prefill:
          basketName.toLowerCase() === prefilledKeywordRef.current.trim().toLowerCase(),
        suggested_name: prefilledKeywordRef.current,
      });
      captureBsiEvent('topic_placed', { method: 'ghost_basket' });
      void supabase.auth
        .getSession()
        .then(({ data: { session } }) => {
          if (!session?.access_token) return;

          return fetch('/api/brevo-contact', {
            method: 'POST',
            keepalive: true,
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ action: 'sync', timezone: getBrowserTimeZone() }),
          }).then((response) => {
            if (!response.ok) {
              console.warn('Brevo placement sync failed:', response.status);
            }
          });
        })
        .catch((error) => {
          console.warn('Brevo placement sync error:', error);
        });
      captureConnectionsEvent('basket_anchor_topic_set', {
        basket_id: createdBasket.id,
        topic_id: topic.id,
        is_anchor: true,
        source: 'create_and_place',
      });

      if (!shouldShowRotationalOverlay(user?.id)) {
        onClose();
        navigate(`/workspace/${workspaceId}`, {
          state: {
            activeTab: 'Baskets',
            workspaceName: currentWorkspace?.name || 'Workspace',
            shouldScrollToBasket: createdBasket.id
          }
        });
      } else {
        setPlacementCelebration({ scrollToBasketId: createdBasket.id });
      }
    } catch (error) {
      // unwrap() rejects with the rejectWithValue string; the user stays on
      // BSI with their search query intact.
      console.error('Failed to create basket and place topic:', error);
      toast.error(
        typeof error === 'string'
          ? error
          : error?.message || 'Failed to create basket and place topic'
      );
    } finally {
      setIsCreating(false);
    }
  };

  // Update the filter tabs section
  const handleScroll = (e) => {
    const container = e.currentTarget;
    const isScrollable = container.scrollWidth > container.clientWidth;
    
    if (isScrollable) {
      container.style.paddingBottom = '4px'; // Add padding to show scrollbar
    } else {
      container.style.paddingBottom = '0px';
    }
  };

  // Extract keywords from highlighted concept for context-aware placeholder
  const highlightedConcept = topic?.concepts?.find(concept => concept.is_highlighted);
  const searchKeywords = useMemo(() => {
    if (!highlightedConcept?.text) return [];
    return extractKeywords(highlightedConcept.text);
  }, [highlightedConcept]);

  // Generate dynamic placeholder text
  const searchPlaceholder = useMemo(() => {
    if (searchKeywords.length === 0) {
      return 'Try "keyword", "keyword"';
    }
    const quoted = searchKeywords.map((k) => `"${k}"`).join(', ');
    return `Try ${quoted}`;
  }, [searchKeywords]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-7xl h-[98vh] sm:h-[95vh] max-h-[98vh] sm:max-h-[95vh] flex flex-col">
        {/* Header - spans full width */}
        <div className="flex-shrink-0 mb-4">
          <Header onClose={onClose} />
        </div>

        {/* Two-column layout */}
        <div className="flex-1 min-h-0 flex flex-col lg:flex-row gap-4 lg:gap-6">
          {/* Left Column - Topic Details and Search */}
          <div className="flex flex-col lg:w-2/5 xl:w-2/5 space-y-4 min-h-0 bg-slate-100/40 dark:bg-slate-900/30 rounded-lg p-4 relative">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-emerald-500" aria-hidden="true" />
              <span className="text-xs font-semibold tracking-wide uppercase text-neutral-700 dark:text-neutral-300">
                Studying now
              </span>
            </div>
            {/* Topic Info - aligned with first row of baskets */}
            <div className="flex-shrink-0 pt-[60px] mb-6">
              <TopicInfo topic={topic} currentBasketId={currentBasketId} baskets={baskets} />
            </div>
            
            {/* Search Bar */}
            <div className="flex-shrink-0">
              <label
                htmlFor="bsi-search"
                className="block text-base font-medium text-neutral-900 mb-2"
              >
                What's the buzzword in your hard detail?
              </label>
              <Searchbar 
                id="bsi-search"
                searchTerm={searchTerm} 
                setSearchTerm={handleSearchTermChange}
                onClearSearch={handleClearSearch}
                placeholder={searchPlaceholder}
                inputRef={searchInputRef}
              />
            </div>

            {/* Note about finding keywords */}
            <div className="flex-shrink-0 space-y-2">
              <p className="text-xs text-neutral-600 dark:text-neutral-400 italic leading-snug">
                <span className="font-semibold not-italic text-neutral-700 dark:text-neutral-300">
                  Tip:
                </span>{' '}
                Search a keyword from your hard detail to find a basket, or create a new basket named after the hard detail.
              </p>
              <button
                type="button"
                onClick={handleExamplesModalOpen}
                aria-haspopup="dialog"
                aria-expanded={showExamplesModal}
                className="
                  text-sm font-medium text-cranberry-800 underline underline-offset-2
                  hover:text-cranberry-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
                  focus-visible:outline-cranberry-700 dark:text-cranberry-400 dark:hover:text-cranberry-300
                "
              >
                See examples
              </button>
            </div>

            {/* Pro tips carousel: strong basket first, then placing an item */}
            <div className="flex-shrink-0">
              <TipBoxCarousel
                pageId="basket-selection"
                tipId="basket-selection-pro-tips"
                tips={basketSelectionProTips}
                position="top"
                dismissible={true}
                centerInViewport
                showProTipCounter={false}
                showAgainLabel="What makes a strong basket?"
              />
            </div>
          </div>

          {/* Vertical divider */}
          <div className="hidden lg:block w-px bg-neutral-300 dark:bg-neutral-700"></div>

          {/* Right Column - Basket List */}
          <div className="flex flex-col lg:w-3/5 xl:w-3/5 min-h-0">
            <div className="flex items-center gap-2 mb-2">
              <span className="h-2 w-2 rounded-full bg-amber-500" aria-hidden="true" />
              <span className="text-xs font-semibold tracking-wide uppercase text-neutral-700 dark:text-neutral-300">
                Reviewing as you scan
              </span>
            </div>
            {/* Filter tabs */}
            <div className="flex-shrink-0 mb-3">
              <div 
                className="flex overflow-x-auto hide-scrollbar" 
                onScroll={handleScroll}
                style={{ 
                  WebkitOverflowScrolling: 'touch',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none'
                }}
              >
                <div className="flex gap-1.5 p-1 min-w-min">
                  {Object.values(BASKET_FILTERS).map((filter) => (
                    <TabButton
                      key={filter}
                      active={basketFilter === filter}
                      onClick={() => setBasketFilter(filter)}
                      count={getFilterCounts[filter]}
                      className="whitespace-nowrap"
                    >
                      {filter.replace(' Baskets', '')}
                    </TabButton>
                  ))}
                </div>
              </div>
            </div>

            {/* Filtered-state row: labels the search so the grid doesn't read
                as an empty account, with a one-tap way back to all baskets */}
            {trimmedSearchTerm && (
              <div className="flex-shrink-0 mb-2 flex items-center justify-between gap-2">
                <p className="text-sm text-neutral-600 dark:text-neutral-400 truncate min-w-0">
                  Search: <span className="font-medium text-neutral-800 dark:text-neutral-200">&quot;{trimmedSearchTerm}&quot;</span>
                </p>
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="
                    flex-shrink-0 text-sm font-medium text-cranberry-800 underline underline-offset-2
                    hover:text-cranberry-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
                    focus-visible:outline-cranberry-700 dark:text-cranberry-400 dark:hover:text-cranberry-300
                  "
                >
                  Show all baskets
                </button>
              </div>
            )}

            {/* Basket grid with flex-1 to take remaining space */}
            <div className="flex-1 min-h-0 overflow-y-auto">
              <BasketGrid 
                baskets={filteredBaskets} 
                selectedBasketId={selectedBasketId} 
                onBasketSelect={handleBasketSelect}
                currentBasketId={currentBasketId}
                searchTerm={searchTerm}
                onBasketMenuClick={handleBasketMenuClick}
                hasNoOtherBaskets={hasNoOtherBaskets}
                showFilterEmptyMessage={showFilterEmptyMessage}
                showGhostBasket={showGhostBasket && isWorkspaceLoaded && Boolean(workspaceId)}
                ghostBasketName={trimmedSearchTerm}
                ghostBasketTopic={topic}
                onGhostBasketCreate={handleGhostBasketCreate}
                isCreatingGhostBasket={isCreating}
              />
            </div>

            {/* Move button at the bottom */}
            <div className="flex-shrink-0 mt-4">
              <MoveButton 
                onClick={handleMove} 
                disabled={!selectedBasketId || selectedBasketId === currentBasketId} 
              />
            </div>
          </div>
        </div>

        {showRelationModal && (
          <RelationDescriptionModal
            onClose={() => {
              if (!isMoveInProgress) {
                setShowRelationModal(false);
              }
            }}
            onSubmit={performTopicMove}
            onComplete={finishMoveAndReturnToWorkspace}
            topic={topic}
            basketName={baskets.find(b => b.id === selectedBasketId)?.name || ''}
            basketTopics={baskets.find(b => b.id === selectedBasketId)?.topics || []}
          />
        )}

        <BasketSelectionExamplesModal
          isOpen={showExamplesModal}
          onClose={handleExamplesModalClose}
        />

        {placementCelebration && (
          <BasketPlacementCelebrationOverlay
            userId={user?.id}
            movedTopicName={topic?.name || 'this topic'}
            onContinue={handlePlacementCelebrationContinue}
            onRemindAgain={handlePlacementCelebrationRemind}
          />
        )}
      </div>
    </div>
  );
};

export default BasketSelectionInterface;
